/**
 * The road a race is run on (SPEC-008 PR-8.1).
 *
 * Three closed vocabularies and the physical constants each value carries. They
 * are closed for the reason `carSchema.js`'s are: every value reaches the screen
 * as a WORD through a composed message key (`compare.surface.snow`), and a value
 * outside the set would render the key itself. It also keeps the URL honest —
 * these three end up in the query string, where anybody can type them.
 *
 * A free numeric gradient was the alternative and it is worse in every
 * direction: it is a number to validate, a label to compose and a range to
 * defend, in service of a reader who wants 11% and does not exist.
 *
 * Pure, free of every other module in the tree, and deliberately not aware that
 * a car exists. Everything here is a fact about tarmac, water, snow and gravity.
 */

/** Standard gravity, and the only reason this module knows a car has weight. */
export const GRAVITY = 9.80665;

/** Sea level, twenty degrees. Every drag figure in this app is against this. */
export const AIR_DENSITY = 1.2;

/**
 * What each surface does to a tyre, in three numbers rather than one.
 *
 * `peak` is what the tyre holds before it lets go. `slide` is what it holds
 * once it has — always lower, which is the entire reason a spinning wheel is
 * slower than a gripping one and therefore the entire reason traction control
 * exists. `rolling` is what the surface costs even when nothing is slipping.
 *
 * Snow is the case that proves all three are needed and not one: it has a
 * quarter of the dry grip, keeps under two thirds of THAT once a wheel spins,
 * and costs nearly three times the rolling drag. Modelled with a single grip
 * coefficient it would draw as a slippery road; it is not a slippery road, it is
 * a different problem, and the difference is why an all-wheel-drive car walks
 * away from a rear-drive one on it.
 *
 * The dry peak is 1.0 exactly and that is a choice worth naming: it is the
 * reference the per-car grip factor in `carDynamics.js` is measured against, so
 * a factor of 1.3 reads as "this car's tyres and weight distribution beat plain
 * dry tarmac by thirty percent" rather than as an offset against an arbitrary
 * baseline.
 */
export const SURFACES = {
    dry: { peak: 1.0, slide: 0.72, rolling: 0.013 },
    wet: { peak: 0.62, slide: 0.45, rolling: 0.016 },
    snow: { peak: 0.25, slide: 0.16, rolling: 0.035 },
};

/**
 * The road's slope, as a FRACTION — rise over run, the way a road sign gives it.
 *
 * Not degrees. 8% is a sign a driver has actually read; 4.6° is a number nobody
 * has ever seen on a hill. The arithmetic wants an angle and takes the arctangent
 * where it needs one, which is one conversion in one place.
 *
 * Eight percent rather than something steeper because of what happens at the
 * bottom of the catalogue: at twelve percent on snow a rear-drive car cannot
 * leave the line at all, and twenty of the twenty-four cars here are rear-drive.
 * "Nothing moves" is a true answer and it is not an interesting screen.
 */
export const GRADIENTS = {
    flat: 0,
    climb: 0.08,
};

/**
 * Whether the car is allowed to break traction.
 *
 * The model is the honest simple one: with it on, drive force is capped just
 * under the peak the tyre can hold, so the car never spins; with it off, the
 * full engine demand is applied and anything past the peak drops the whole
 * contact patch to the sliding coefficient.
 *
 * What follows is not "the aid is always faster", and the exception is the
 * interesting part. A car that never reaches the limit gets the margin back by
 * switching it off — a hundredth of a second, once, for the lightest car in the
 * catalogue on dry tarmac — while a car that crosses it loses whole seconds. So
 * the control is a way of ASKING WHICH KIND OF CAR THIS IS on this surface, and
 * the answer for most of them changes the moment the road is wet.
 *
 * Two things are assumptions rather than claims about period engineering, and
 * both are stated rather than hidden: the electronics here are perfect and the
 * driver is not, and most of these cars never had traction control at all.
 */
export const TRACTION_CONTROL = ['on', 'off'];

/**
 * How much of the peak traction control leaves on the table.
 *
 * A system that targeted the peak exactly would cross it, because it can only
 * react to slip that has already begun. Giving up a twentieth is what makes "on"
 * a guarantee rather than an intention, and it is why an aid can be marginally
 * slower than a perfect launch that never happens.
 */
export const TRACTION_CONTROL_TARGET = 0.95;

export const SURFACE_KEYS = Object.keys(SURFACES);
export const GRADIENT_KEYS = Object.keys(GRADIENTS);

/**
 * The conditions a race is run under, parsed rather than trusted.
 *
 * These arrive from the query string, so an unknown value is not an error — it
 * is a link somebody edited, or one written against a version of this app that
 * offered something else. Each field falls back to the first value of its own
 * vocabulary, which is the dry, flat, aided race: the conditions every figure in
 * the catalogue was measured under, and therefore the only defensible default.
 */
export function raceConditions({ surface, gradient, tractionControl } = {}) {
    return {
        surface: SURFACE_KEYS.includes(surface) ? surface : SURFACE_KEYS[0],
        gradient: GRADIENT_KEYS.includes(gradient) ? gradient : GRADIENT_KEYS[0],
        tractionControl: TRACTION_CONTROL.includes(tractionControl) ? tractionControl : TRACTION_CONTROL[0],
    };
}

/** The three constants a parsed condition resolves to, ready for the integrator. */
export function conditionPhysics(conditions) {
    const { peak, slide, rolling } = SURFACES[conditions.surface];
    const slope = GRADIENTS[conditions.gradient];
    const angle = Math.atan(slope);

    return {
        peak,
        slide,
        rolling,
        // Pre-resolved because the integrator asks for both several hundred
        // times a race and neither one changes inside a run.
        gradeSine: Math.sin(angle),
        gradeCosine: Math.cos(angle),
        isAided: conditions.tractionControl === 'on',
    };
}
